import React, { useMemo, useState } from 'react';
import { FlightTabs, FlightTabPanel, FlightButton } from '@flybits/design-system';
import './Analytics.scss';
import AnalyticsDashboard from './Overview/AnalyticsDashboard';
import CustomAnalyticsDashboard from './CustomAnalytics/Dashboard/CustomAnalyticsDashboard';
import LiveAnalyticsDashboard from './LiveAnalytics/Dashboard/LiveAnalyticsDashboard';
import { useHistory, useParams } from 'react-router-dom';
import { TAnalyticsRouteParams } from 'pages/Analytics/types';
import useFeatureFlag from 'hooks/useFeatureFlag';

// CLASSES
const CLASS_MAIN = 'analytics-container';
const CLASSES = {
  BODY: `${CLASS_MAIN}__body`,
  HEADER: `${CLASS_MAIN}__header`,
  CONTAINER: `${CLASS_MAIN}__container`,
  TABS: `${CLASS_MAIN}__tabs`,
  ADDBUTTON: `${CLASS_MAIN}__addbutton`,
};

function Analytics() {
  const [selectedTab, setSelectedTab] = useState('overview');
  const history = useHistory();
  const { pid } = useParams<TAnalyticsRouteParams>();
  const { flags } = useFeatureFlag();
  const showCustomAnalyticsTab = flags['tx_ca_custom_module'];
  const showLiveAnalyticsTab = flags['tx_live_analytics'];

  // Hide the tab bar only when both optional tabs are off; each tab is gated by
  // its own flag so they can be rolled out independently.
  const tabs = useMemo(() => {
    if (!showCustomAnalyticsTab && !showLiveAnalyticsTab) {
      return null;
    }
    const list = [{ key: 'overview', name: 'Overview' }];
    if (showCustomAnalyticsTab) {
      list.push({ key: 'custom', name: 'Custom analytics' });
    }
    if (showLiveAnalyticsTab) {
      list.push({ key: 'live', name: 'Live analytics' });
    }
    return list;
  }, [showCustomAnalyticsTab, showLiveAnalyticsTab]);

  useMemo(() => {
    const defaultToCustomAnalyticsTab = new URLSearchParams(window.location.search).has('showCustomAnalyticsTab');
    showCustomAnalyticsTab && defaultToCustomAnalyticsTab && setSelectedTab('custom');
  }, [showCustomAnalyticsTab]);

  return (
    <main className={CLASSES.BODY}>
      {tabs && (
        <div className={CLASSES.HEADER}>
          <div className={CLASSES.TABS}>
            <FlightTabs
              labels={tabs}
              selectedTab={selectedTab}
              onSelect={(e) => {
                setSelectedTab(e.key.toString());

                if (e.key === 'overview') {
                  // Remove all url params
                  window.history.replaceState(null, '', window.location.pathname);
                }
              }}
            />
          </div>

          {selectedTab === 'custom' && ( // Only show if it is experience dashboard
            <div className={CLASSES.ADDBUTTON}>
              <FlightButton
                type="submit"
                iconLeft="add"
                label="Add chart"
                onClick={() => history.push(`/project/${pid}/analytics/moduleconfig`)}
              ></FlightButton>
            </div>
          )}
        </div>
      )}

      <div className={CLASSES.CONTAINER}>
        {selectedTab === 'overview' && ( // The first item here is the Overview tab
          <FlightTabPanel>
            <AnalyticsDashboard />
          </FlightTabPanel>
        )}

        {selectedTab === 'custom' && showCustomAnalyticsTab && (
          <FlightTabPanel>
            <CustomAnalyticsDashboard />
          </FlightTabPanel>
        )}

        {selectedTab === 'live' && showLiveAnalyticsTab && (
          <FlightTabPanel>
            <LiveAnalyticsDashboard />
          </FlightTabPanel>
        )}
      </div>
    </main>
  );
}

export default Analytics;
